import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { FileText, Home, LogOut, Users, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotations');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    fetchData();
  }, [selectedStatus]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const statusParam = selectedStatus !== 'all' ? `?status=${selectedStatus}` : '';
      const [quotesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/quotations/all${statusParam}`, { headers }),
        axios.get(`${API}/projects/all`, { headers })
      ]);
      setQuotations(quotesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateQuotationStatus = async (quotationId, newStatus) => {
    try {
      const headers = getAuthHeader();
      await axios.patch(
        `${API}/quotations/${quotationId}/status?status=${newStatus}`,
        {},
        { headers }
      );
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const createProject = async (quotationId) => {
    try {
      const headers = getAuthHeader();
      await axios.post(
        `${API}/projects/?quotation_id=${quotationId}`,
        {},
        { headers }
      );
      toast.success('Project created successfully');
      fetchData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    try {
      const headers = getAuthHeader();
      await axios.patch(
        `${API}/projects/${projectId}/status?status=${newStatus}`,
        {},
        { headers }
      );
      toast.success('Project status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      in_progress: 'bg-purple-100 text-purple-800',
      planning: 'bg-blue-100 text-blue-800',
      permits: 'bg-yellow-100 text-yellow-800',
      construction: 'bg-orange-100 text-orange-800',
      inspection: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = {
    totalQuotations: quotations.length,
    pendingQuotations: quotations.filter(q => q.status === 'pending').length,
    activeProjects: projects.filter(p => p.status !== 'completed').length,
    completedProjects: projects.filter(p => p.status === 'completed').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_9ca81a2a-0a4d-4b76-b02e-3997c1e64651/artifacts/ypha2bz9_%E5%BE%AE%E4%BF%A1%E5%9B%BE%E7%89%87_20260309130435_160_131.png" 
                alt="ALIGCUS Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <div className="text-right mr-4">
                <p className="text-sm text-gray-600">Employee Portal</p>
                <p className="font-semibold text-gray-900">{user?.full_name}</p>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Card */}
        <Card className="mb-8 bg-gradient-to-r from-red-600 to-red-700 text-white border-0">
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold mb-2">Employee Dashboard</h1>
            <p className="text-red-100">Manage customer quotations and projects</p>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Quotes</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalQuotations}</p>
                </div>
                <FileText className="w-12 h-12 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Review</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.pendingQuotations}</p>
                </div>
                <Clock className="w-12 h-12 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Projects</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.activeProjects}</p>
                </div>
                <Home className="w-12 h-12 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.completedProjects}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('quotations')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'quotations'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quotations ({quotations.length})
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Projects ({projects.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Quotations Tab */}
        {activeTab === 'quotations' && (
          <div>
            {/* Filter */}
            <div className="mb-4">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="space-y-4">
              {quotations.map((quote) => (
                <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 mr-3">
                            {quote.customer_name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(quote.status)}`}>
                            {quote.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">{quote.customer_email} • {quote.customer_phone}</p>
                        <p className="text-gray-600 mb-2">{quote.property_address}, {quote.property_city}, {quote.property_state}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{quote.bedrooms} Bed / {quote.bathrooms} Bath</span>
                          <span>{quote.square_footage} sq ft</span>
                          <span>{quote.package_type}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                        <p className="text-2xl font-bold text-red-600">${quote.total_price.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 pt-4 border-t">
                      {quote.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => updateQuotationStatus(quote.id, 'reviewed')}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Mark Reviewed
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => updateQuotationStatus(quote.id, 'approved')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuotationStatus(quote.id, 'rejected')}
                            className="border-red-600 text-red-600 hover:bg-red-50"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {quote.status === 'reviewed' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => updateQuotationStatus(quote.id, 'approved')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuotationStatus(quote.id, 'rejected')}
                            className="border-red-600 text-red-600 hover:bg-red-50"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {quote.status === 'approved' && (
                        <Button
                          size="sm"
                          onClick={() => createProject(quote.id)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Create Project
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <h3 className="text-xl font-semibold text-gray-900 mr-3">{project.customer_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                          {project.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">Started: {new Date(project.start_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="mt-4 mb-4">
                    <div className="flex items-center space-x-2">
                      {['planning', 'permits', 'construction', 'inspection', 'completed'].map((stage, idx) => {
                        const isActive = ['planning', 'permits', 'construction', 'inspection', 'completed'].indexOf(project.status) >= idx;
                        return (
                          <React.Fragment key={stage}>
                            <div className={`flex-1 h-2 rounded ${isActive ? 'bg-red-600' : 'bg-gray-200'}`} />
                            {idx < 4 && <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-red-600' : 'bg-gray-200'}`} />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-4 border-t">
                    <select
                      value={project.status}
                      onChange={(e) => updateProjectStatus(project.id, e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="planning">Planning</option>
                      <option value="permits">Permits</option>
                      <option value="construction">Construction</option>
                      <option value="inspection">Inspection</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;
